import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, update, onValue } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import { useLanguage } from '../LanguageContext';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    const u = JSON.parse(savedUser);
    setUser(u);
    setName(u.name || '');
    setCity(u.city || '');
    setVehicle(u.vehicle || '');
  }, [navigate]);

  useEffect(() => {
    if (!user || !user.userId) return;
    const userRef = ref(db, 'users/' + user.userId);
    const unsubscribe = onValue(userRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const updated = { ...user, ...data, userId: user.userId };
        setUser(updated);
        localStorage.setItem('guningo_user', JSON.stringify(updated));
      }
    });
    return () => unsubscribe();
  }, [user?.userId]);

  const isGuest = user && (!user.name || user.name === 'Guest User');

  const saveChanges = async () => {
    if (isGuest) {
      navigate('/');
      return;
    }
    setSaving(true);
    try {
      await update(ref(db, 'users/' + user.userId), {
        name: name.trim(), city: city.trim(), vehicle: vehicle.trim(),
      });
      const updated = { ...user, name: name.trim(), city: city.trim(), vehicle: vehicle.trim() };
      setUser(updated);
      localStorage.setItem('guningo_user', JSON.stringify(updated));
      setSaving(false);
      alert('Profile updated!');
    } catch (error) {
      setSaving(false);
      alert('Error: ' + error.message);
    }
  };

  const handleDocUpload = async (docType, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      try {
        await update(ref(db, 'users/' + user.userId), {
          [docType + 'Photo']: base64,
          [docType + 'Status']: 'pending',
        });
        alert('Document uploaded — it will be reviewed shortly.');
      } catch (error) {
        alert('Error uploading: ' + error.message);
      }
    };
    reader.readAsDataURL(file);
  };

  if (!user) return null;

  if (isGuest) {
    return (
      <div style={styles.page}>
        <Navbar userName={user.name} userPhone={user.phone} />
        <div style={styles.guestWrap}>
          <div style={styles.guestIcon}>👤</div>
          <h2>You're browsing as a guest</h2>
          <p style={{ color: '#9499b5' }}>Login to book rides and manage your profile</p>
          <button style={styles.primaryBtn} onClick={() => navigate('/')}>Login / Sign Up</button>
        </div>
      </div>
    );
  }

  const docTypes = [
    { key: 'license', label: 'Driving License' },
    { key: 'aadhaar', label: 'Aadhaar Card' },
    { key: 'rc', label: 'Vehicle RC' },
  ];

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />
      <div style={styles.container}>

        <div style={styles.profileHeader}>
          <div style={styles.avatar}>{user.initials || '??'}</div>
          <div>
            <h2 style={styles.userName}>{user.name}</h2>
            <p style={styles.userMeta}>{user.city}</p>
          </div>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>{t('editProfile')}</h3>
          <label style={styles.fieldLabel}>{t('fullName')}</label>
          <input style={styles.input} value={name} onChange={e => setName(e.target.value)} />
          <label style={styles.fieldLabel}>{t('city')}</label>
          <input style={styles.input} value={city} onChange={e => setCity(e.target.value)} />
          <label style={styles.fieldLabel}>{t('vehicle')}</label>
          <input style={styles.input} value={vehicle} onChange={e => setVehicle(e.target.value)} placeholder="e.g. Swift Dzire White" />
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>{t('documents')}</h3>
          {docTypes.map(doc => {
            const status = user[doc.key + 'Status'];
            return (
              <div key={doc.key} style={styles.docRow}>
                <div style={styles.docTop}>
                  <span style={styles.docLabel}>{doc.label}</span>
                  <span style={{
                    ...styles.docStatus,
                    color: status === 'verified' ? '#166534' : status === 'pending' ? '#8a6416' : '#9499b5'
                  }}>
                    {status === 'verified' ? '✓ Verified' : status === 'pending' ? '⏳ Pending' : 'Not uploaded'}
                  </span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  style={styles.fileInput}
                  onChange={e => handleDocUpload(doc.key, e.target.files[0])}
                />
              </div>
            );
          })}
        </div>

        <button style={styles.saveBtn} onClick={saveChanges} disabled={saving}>
          {saving ? 'Saving...' : t('save')}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  container: { maxWidth: 520, margin: '0 auto', padding: '30px 20px 40px' },
  profileHeader: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  avatar: {
    width: 64, height: 64, borderRadius: 32, background: '#eaf5fc', color: '#3a8fd0',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700,
  },
  userName: { color: '#1a1a2e', margin: 0 },
  userMeta: { color: '#9499b5', margin: '2px 0 0' },
  card: { border: '1px solid #eef1f8', borderRadius: 14, padding: 20, marginBottom: 16, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' },
  cardTitle: { margin: '0 0 14px', color: '#1a1a2e' },
  fieldLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#5a5f7d', marginBottom: 5, marginTop: 10 },
  input: { width: '100%', height: 44, borderRadius: 8, border: '1.5px solid #d9edfa', padding: '0 12px', fontSize: 14, boxSizing: 'border-box' },
  docRow: { borderBottom: '1px solid #f0f0f0', padding: '10px 0' },
  docTop: { display: 'flex', justifyContent: 'space-between', marginBottom: 8 },
  docLabel: { fontSize: 13, fontWeight: 500, color: '#1a1a2e' },
  docStatus: { fontSize: 12, fontWeight: 600 },
  fileInput: { fontSize: 12 },
  saveBtn: { width: '100%', height: 50, background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  guestWrap: { textAlign: 'center', padding: '80px 20px' },
  guestIcon: { fontSize: 50, marginBottom: 14 },
  primaryBtn: { background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontWeight: 700, cursor: 'pointer', marginTop: 16 },
};